import { useEffect, useRef, useState } from 'react';

export interface Coords {
  lat: number;
  lng: number;
  accuracy?: number;
  heading?: number;
}

export type GeoState =
  | { status: 'idle' }
  | { status: 'locating' }
  | { status: 'ok'; coords: Coords }
  | { status: 'denied' }
  | { status: 'unavailable'; message: string };

/**
 * Posição contínua do aparelho.
 *
 * `watchPosition` e não `getCurrentPosition` em laço: o navegador só
 * entrega atualização quando o aparelho de fato se move, o que gasta menos
 * bateria — que é o recurso mais escasso de um motorista em turno.
 */
export function useGeolocation(enabled: boolean): GeoState & { retry: () => void } {
  const [state, setState] = useState<GeoState>({ status: 'idle' });
  // Permite pedir a localização de novo depois de uma negativa: em alguns
  // navegadores a segunda tentativa reabre o pedido de permissão, e em
  // todos ela recupera um erro temporário de sinal.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'idle' });
      return;
    }
    if (!('geolocation' in navigator)) {
      setState({ status: 'unavailable', message: 'este aparelho não tem GPS disponível' });
      return;
    }

    setState({ status: 'locating' });

    const id = navigator.geolocation.watchPosition(
      (pos) =>
        setState({
          status: 'ok',
          coords: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            heading: pos.coords.heading ?? undefined,
          },
        }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setState({ status: 'denied' });
        else setState({ status: 'unavailable', message: err.message });
      },
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 20_000 },
    );

    return () => navigator.geolocation.clearWatch(id);
  }, [enabled, attempt]);

  return { ...state, retry: () => setAttempt((n) => n + 1) };
}

/**
 * Mantém a tela acesa enquanto o motorista está online.
 *
 * PWA não tem geolocalização em segundo plano: com a tela apagada ou o app
 * fora de foco, o GPS para e o passageiro deixa de ver o carro se mexendo.
 * O wake lock é o que torna o app do motorista viável na web — junto com o
 * celular no suporte do painel, que é como se trabalha de verdade.
 *
 * O sistema solta o lock sozinho quando a aba perde o foco, então
 * readquirimos ao voltar.
 */
export function useWakeLock(enabled: boolean): { supported: boolean; active: boolean } {
  const sentinel = useRef<WakeLockSentinel | null>(null);
  const [active, setActive] = useState(false);
  const supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;

  useEffect(() => {
    if (!enabled || !supported) {
      void sentinel.current?.release().catch(() => undefined);
      sentinel.current = null;
      setActive(false);
      return;
    }

    let cancelled = false;

    const acquire = async () => {
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          void lock.release().catch(() => undefined);
          return;
        }
        sentinel.current = lock;
        setActive(true);
        lock.addEventListener('release', () => setActive(false));
      } catch {
        setActive(false);
      }
    };

    void acquire();

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !sentinel.current) void acquire();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void sentinel.current?.release().catch(() => undefined);
      sentinel.current = null;
    };
  }, [enabled, supported]);

  return { supported, active };
}

/** Vibração curta para confirmação tátil. Silenciosa onde não existe. */
export function haptic(pattern: number | number[] = 12): void {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    navigator.vibrate(pattern);
  }
}

/**
 * Um tom sozinho, sintetizado — não um arquivo de áudio.
 *
 * Um .mp3 de campainha precisaria ser gravado, servido, versionado e
 * baixado no primeiro toque; um osc do Web Audio é código, não asset, e
 * não pesa nada no 3G do interior. Best effort: iOS em modo silencioso e
 * navegador que bloqueou autoplay sem gesto do usuário não tocam nada, e
 * a chamada não deve derrubar quem chamou por isso.
 */
let audioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext | null {
  const Ctx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  audioCtx ??= new Ctx();
  return audioCtx;
}

/**
 * Vários tons numa chamada só, agendados no relógio do próprio Web
 * Audio (`ctx.currentTime + at`), não em `setTimeout`.
 *
 * A versão anterior encadeava `setTimeout` para cada tom extra, chamada
 * de novo a cada pulso da oferta (a cada 900ms, depois a cada 420ms). Sob
 * essa cadência, os timers se acumulavam e o som parava de tocar depois
 * de alguns pulsos. Agendado no relógio de áudio, o navegador toca tudo
 * sozinho, sem depender do event loop do JavaScript continuar pontual.
 *
 * Sempre espera o `resume()` antes de agendar: contexto suspenso (aba em
 * segundo plano, política de autoplay) engolia o primeiro som em
 * silêncio quando o `start()` seguia em frente sem esperar.
 */
export function chime(
  tones: { freq: number; at: number; durationMs: number; gain?: number }[],
): void {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;

    const schedule = () => {
      const base = ctx.currentTime;
      for (const t of tones) {
        const osc = ctx.createOscillator();
        const vol = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = t.freq;
        vol.gain.value = t.gain ?? 0.15;
        const start = base + t.at / 1000;
        const end = start + t.durationMs / 1000;
        // Fade de saída: sem ele, cortar a onda no meio faz um estalo
        // seco em vez de um bipe limpo.
        vol.gain.setValueAtTime(vol.gain.value, start);
        vol.gain.exponentialRampToValueAtTime(0.001, end);
        osc.connect(vol).connect(ctx.destination);
        osc.start(start);
        osc.stop(end);
      }
    };

    if (ctx.state === 'suspended') void ctx.resume().then(schedule);
    else schedule();
  } catch {
    // Sem áudio, sem drama: o resto da tela (vibração, barra, cor)
    // ainda carrega o aviso.
  }
}
